import { useSearchParams } from 'react-router-dom'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { usePermission } from '@/hooks/usePermission'
import { SubscriptionPage } from '@/routes/SubscriptionPage'
import { RolesPage } from '@/routes/roles/RolesPage'
import { TrashPage } from '@/routes/trash/TrashPage'
import { UsersPage } from '@/routes/users/UsersPage'
import { PERMISSION_KEYS } from '@/stores/permission.store'

import { AuditLogTab } from './AuditLogTab'
import { GeneralTab } from './GeneralTab'
import { MailboxesPage } from './MailboxesPage'

/** Everything about the workspace itself, one tab per area; a tab shows only if the user may use it. */
export function WorkspaceSettingsPage() {
  const { canAnywhere, isOwner, isAdmin } = usePermission()
  const [params, setParams] = useSearchParams()

  const tabs = [
    { id: 'general', label: 'General', show: true },
    { id: 'users', label: 'Users', show: canAnywhere(PERMISSION_KEYS.USER_VIEW) },
    { id: 'roles', label: 'Roles', show: canAnywhere(PERMISSION_KEYS.ROLE_VIEW) },
    // Everyone has personal mailboxes; the page itself hides workspace ones without the permission
    { id: 'mailboxes', label: 'Mailboxes', show: true },
    { id: 'billing', label: 'Billing', show: canAnywhere(PERMISSION_KEYS.SUBSCRIPTION_VIEW) },
    {
      id: 'trash', label: 'Trash',
      show: [PERMISSION_KEYS.RECORD_DELETE, PERMISSION_KEYS.RECORD_GROUP_DELETE, PERMISSION_KEYS.AUTOMATION_DELETE].some((k) => canAnywhere(k)),
    },
    { id: 'audit', label: 'Audit log', show: isOwner || isAdmin },
  ].filter((t) => t.show)

  const requested = params.get('tab') ?? 'general'
  const active = tabs.some((t) => t.id === requested) ? requested : 'general'

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Workspace settings</h1>
        <p className="text-sm text-muted-foreground">Manage this workspace, its people, billing and history.</p>
      </div>
      <Tabs value={active} onValueChange={(tab) => setParams({ tab }, { replace: true })}>
        <TabsList className="h-auto flex-wrap justify-start">
          {tabs.map((t) => <TabsTrigger key={t.id} value={t.id}>{t.label}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="general" className="mt-4"><GeneralTab canEdit={canAnywhere(PERMISSION_KEYS.WORKSPACE_UPDATE)} isOwner={isOwner} /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersPage /></TabsContent>
        <TabsContent value="roles" className="mt-4"><RolesPage /></TabsContent>
        <TabsContent value="mailboxes" className="mt-4"><MailboxesPage /></TabsContent>
        <TabsContent value="billing" className="mt-4"><SubscriptionPage /></TabsContent>
        <TabsContent value="trash" className="mt-4"><TrashPage /></TabsContent>
        <TabsContent value="audit" className="mt-4"><AuditLogTab /></TabsContent>
      </Tabs>
    </div>
  )
}
